import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowLeft, FiCheck, FiEye, FiEyeOff, FiX, FiLock,
  FiBarChart2, FiTarget, FiUsers, FiActivity, FiTrendingUp, FiCheckCircle,
} from "react-icons/fi";
import "./PmsLogin.css";
import { useRole } from "../../context/RoleContext";
import useEmailLinkToken, { clearLinkToken, formatSeconds } from "../../hooks/useEmailLinkToken";
import {
  resetPassword,
  validateResetLink,
  exchangeResetLink,
  isResetEndpointMissing,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
  passwordPolicy,
} from "../../services/authApi";

const EXPIRED_CODES = ["RESET_TOKEN_INVALID", "RESET_TOKEN_EXPIRED", "RESET_TOKEN_USED", "TOKEN_INVALID", "TOKEN_EXPIRED"];

// Matches PASSWORD_RESET_WINDOW_SECONDS=900 in the backend .env.
// Used only as a fallback when the server does not send a Retry-After header.
const RESET_WINDOW_MINUTES = 15;

function getThrottleMessage(err) {
  const secs = Number(err?.response?.headers?.["retry-after"]);
  if (secs > 0) {
    const mins = Math.ceil(secs / 60);
    return `Too many reset attempts. Please try again in ${mins} minute${mins > 1 ? "s" : ""}.`;
  }
  return `Too many reset attempts. Please try again in up to ${RESET_WINDOW_MINUTES} minutes.`;
}

function BrandMark() {
  return (
    <svg width="38" height="42" viewBox="0 0 38 42" aria-hidden="true" className="pmsx-logo-svg">
      <polygon points="19,1 36,10.5 36,31.5 19,41 2,31.5 2,10.5" fill="#ffffff" fillOpacity="0.14" stroke="#ffffff" strokeWidth="1.8" />
      <rect x="10" y="22" width="4" height="9" rx="1" fill="#ffffff" />
      <rect x="17" y="16" width="4" height="15" rx="1" fill="#ffffff" />
      <rect x="24" y="10" width="4" height="21" rx="1" fill="#4cc3ff" />
    </svg>
  );
}

function AuthNotice({ tone = "info", children }) {
  const icon = tone === "success" ? "✓" : tone === "error" ? "!" : "i";
  return (
    <div className={`pmsx-notice pmsx-notice--${tone}`} role={tone === "error" ? "alert" : "status"}>
      <span className="pmsx-notice-icon" aria-hidden="true">{icon}</span>
      <span>{children}</span>
    </div>
  );
}

function AuthShell({ title, subtitle, label, children }) {
  return (
    <main className="pmsx-root" aria-label={label}>
      <div className="pmsx-decor" aria-hidden="true">
        <span className="pmsx-hex pmsx-hex-1"><FiBarChart2 size={22} /></span>
        <span className="pmsx-hex pmsx-hex-2"><FiTarget size={20} /></span>
        <span className="pmsx-hex pmsx-hex-3"><FiUsers size={20} /></span>
        <span className="pmsx-hex pmsx-hex-4"><FiActivity size={22} /></span>
        <span className="pmsx-hex pmsx-hex-5"><FiTrendingUp size={20} /></span>
        <span className="pmsx-hex pmsx-hex-6"><FiCheckCircle size={18} /></span>
      </div>

      <section className="pmsx-panel">
        <div className="pmsx-brand">
          <BrandMark />
          <span className="pmsx-brand-name">TELIOLABS</span>
        </div>

        <div className="pmsx-heading">
          <h1 className="pmsx-title">{title}</h1>
          {subtitle ? <p className="pmsx-subtitle">{subtitle}</p> : null}
        </div>

        {children}
      </section>

      <footer className="pmsx-footer">
        <p>Not able to login? Contact HR Team</p>
        <a href="mailto:HR@teliolabs.com">HR@teliolabs.com</a>
        <p className="pmsx-copyright">&copy; 2026 Teliolabs. All rights reserved.</p>
      </footer>
    </main>
  );
}

function PasswordInput({ id, label, value, onChange, disabled, autoFocus, onEnter }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="pmsx-field">
      <label className="pmsx-sr-only" htmlFor={id}>{label}</label>
      <FiLock className="pmsx-field-icon" size={17} aria-hidden="true" />
      <input
        id={id}
        type={visible ? "text" : "password"}
        autoComplete="new-password"
        placeholder={label}
        value={value}
        maxLength={PASSWORD_MAX_LENGTH}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => { if (event.key === "Enter" && onEnter) onEnter(event); }}
        disabled={disabled}
        autoFocus={autoFocus}
        className="pmsx-has-toggle"
      />
      <button
        type="button"
        className="pmsx-password-toggle"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        tabIndex={-1}
      >
        {visible ? <FiEye size={17} /> : <FiEyeOff size={17} />}
      </button>
    </div>
  );
}

function Rule({ ok, children }) {
  return (
    <li className={ok ? "is-ok" : ""}>
      {ok ? <FiCheck size={12} aria-hidden="true" /> : <FiX size={12} aria-hidden="true" />}
      <span>{children}</span>
      <span className="pmsx-sr-only">{ok ? " (done)" : " (required)"}</span>
    </li>
  );
}

export default function ResetPasswordPage() {
  // [UPDATED] The e-mail token is first validated and exchanged (GET /validate + POST /exchange, new
  // backend endpoints). `token` below is the short-lived exchange token, not the one from the e-mail.
  const link = useEmailLinkToken("reset", {
    validate: validateResetLink,
    exchange: exchangeResetLink,
    invalidCodes: EXPIRED_CODES,
  });
  const token = link.token;
  const { isAuthenticated, logout } = useRole();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitState, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const linkExpired = link.status === "ready" && link.secondsLeft === 0;
  // Link state decides the screen until the link is ready; after that the form state decides.
  const status = link.status === "checking" ? "checking"
    : link.status === "invalid" || linkExpired ? (submitState === "done" ? "done" : "invalid")
    : link.status === "error" ? "linkError"
    : submitState;

  const rules = useMemo(() => ({
    ...passwordPolicy(password),
    match: password.length > 0 && password === confirm,
  }), [password, confirm]);

  const canSubmit = rules.length && rules.lowercase && rules.uppercase && rules.digit && rules.special && rules.noSpaces && rules.match && status !== "saving";

  const submit = async (event) => {
    event?.preventDefault?.();
    if (!rules.length || !rules.lowercase || !rules.uppercase || !rules.digit || !rules.special || !rules.noSpaces) { setError("Use 8 to 128 characters with uppercase, lowercase, a digit, and a special character; spaces are not allowed."); return; }
    if (!rules.match) { setError("The two passwords don't match."); return; }
    setError("");
    setStatus("saving");
    try {
      await resetPassword({ token, newPassword: password });
      clearLinkToken("reset");
      if (isAuthenticated) logout({ local: true });
      setPassword("");
      setConfirm("");
      setStatus("done");
    } catch (err) {
      const code = err?.response?.data?.error?.code;
      if (isResetEndpointMissing(err)) { setStatus("unavailable"); return; }
      if (EXPIRED_CODES.includes(code) || err?.response?.status === 410) { clearLinkToken("reset"); setStatus("invalid"); return; }
      setStatus("idle");
      if (!err?.response) { setError("Unable to reach the server. Check your connection and try again."); return; }
      if (err.response.status === 429) { setError(getThrottleMessage(err)); return; }
      if (err.response.status === 422) { setError("Password does not meet the required complexity rules."); return; }
      setError(err.response.data?.error?.message || "Could not reset the password. Please try again.");
    }
  };

  if (status === "done") {
    return (
      <AuthShell title="Password changed" label="Teliolabs PMS password changed">
        <div className="pmsx-form">
          <AuthNotice tone="success">
            Your password has been changed and you have been signed out everywhere. Log in with your new password.
          </AuthNotice>
          <Link to="/login" className="pmsx-login-btn pmsx-link-btn">Go to login</Link>
        </div>
      </AuthShell>
    );
  }

  if (status === "invalid") {
    return (
      <AuthShell title="Link not valid" label="Teliolabs PMS reset link not valid">
        <div className="pmsx-form">
          <AuthNotice tone="error">
            This password reset link is invalid, already used, or has expired. Request a new link to continue.
          </AuthNotice>
          <Link to="/forgot-password" className="pmsx-login-btn pmsx-link-btn">Request a new link</Link>
          <Link to="/login" className="pmsx-back-link">
            <FiArrowLeft size={15} aria-hidden="true" />
            <span>Back to login</span>
          </Link>
        </div>
      </AuthShell>
    );
  }

  // [NEW] Shown while GET /validate and POST /exchange are running.
  if (status === "checking") {
    return (
      <AuthShell title="Checking your link" label="Teliolabs PMS checking reset link">
        <div className="pmsx-loading">
          <span className="pmsx-spinner" />
          <span>Checking your reset link...</span>
        </div>
      </AuthShell>
    );
  }

  // [NEW] Network / server problem while checking the link (the link itself may still be fine).
  if (status === "linkError") {
    return (
      <AuthShell title="Reset password" label="Teliolabs PMS reset password">
        <div className="pmsx-form">
          <AuthNotice tone="error">{link.message || "We could not check your reset link. Please try again."}</AuthNotice>
          <button type="button" className="pmsx-login-btn" onClick={link.retry}>Try again</button>
          <Link to="/login" className="pmsx-back-link">
            <FiArrowLeft size={15} aria-hidden="true" />
            <span>Back to login</span>
          </Link>
        </div>
      </AuthShell>
    );
  }

  if (status === "unavailable") {
    return (
      <AuthShell title="Reset password" label="Teliolabs PMS reset password">
        <div className="pmsx-form">
          <AuthNotice tone="info">
            Password reset by e-mail isn't switched on yet. Please ask your administrator to issue a temporary
            password (Users &amp; roles).
          </AuthNotice>
          <Link to="/login" className="pmsx-login-btn pmsx-link-btn">Back to login</Link>
        </div>
      </AuthShell>
    );
  }

  const busy = status === "saving";

  return (
    <AuthShell
      title="Set a new password"
      subtitle="Choose a new password for your Teliolabs PMS account."
      label="Teliolabs PMS reset password"
    >
      <form className="pmsx-form" onSubmit={submit} noValidate>
        {error ? <AuthNotice tone="error">{error}</AuthNotice> : null}
        {link.secondsLeft != null ? (
          <AuthNotice tone="info">
            Please set your password within {formatSeconds(link.secondsLeft)}. For your security the e-mail link has now been used; if time runs out, request a new link.
          </AuthNotice>
        ) : null}

        <PasswordInput id="pmsx-reset-new" label="New password" value={password} onChange={setPassword} disabled={busy} autoFocus />
        <PasswordInput id="pmsx-reset-confirm" label="Confirm new password" value={confirm} onChange={setConfirm} disabled={busy} onEnter={submit} />

        <ul className="pmsx-rules" aria-label="Password rules">
          <Rule ok={rules.length}>{PASSWORD_MIN_LENGTH}-{PASSWORD_MAX_LENGTH} characters</Rule>
          <Rule ok={rules.lowercase}>Lowercase</Rule>
          <Rule ok={rules.uppercase}>Uppercase</Rule>
          <Rule ok={rules.digit}>Digit</Rule>
          <Rule ok={rules.special}>Special character</Rule>
          <Rule ok={rules.noSpaces}>No spaces</Rule>
          <Rule ok={rules.match}>Passwords match</Rule>
        </ul>

        <button type="submit" className="pmsx-login-btn" disabled={!canSubmit}>
          {busy ? (
            <span className="pmsx-btn-loading">
              <span className="pmsx-spinner" />
              <span>Saving...</span>
            </span>
          ) : (
            "Reset password"
          )}
        </button>

        <Link to="/login" className="pmsx-back-link">
          <FiArrowLeft size={15} aria-hidden="true" />
          <span>Back to login</span>
        </Link>
      </form>
    </AuthShell>
  );
}